import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import HelpTip from '@/components/HelpTip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Inbox, Send, ShieldAlert, Trash2, FileEdit, Shield, PenSquare,
  RefreshCw, Copy, Check, Settings2, ArrowLeft, Mail, AlertTriangle, Paperclip, X,
} from 'lucide-react';

const FOLDER_ICONS = {
  inbox: Inbox,
  sent: Send,
  spam: ShieldAlert,
  trash: Trash2,
  drafts: FileEdit,
  quarantine: Shield,
};

const COLOR = {
  gris: 'bg-white/10 text-white/60 border-white/10',
  amarillo: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
  verde: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
};

function when(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function XMailPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState('inbox');
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [composing, setComposing] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState('');
  const [draft, setDraft] = useState({ to: '', cc: '', subject: '', body: '', opportunity_id: '', file_ids: [], quote_ids: [], in_reply_to: '' });

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get('/xmail/me');
      setMe(data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pude cargar X-MAIL.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadList = useCallback(async (f) => {
    try {
      const { data } = await api.get('/xmail/messages', { params: { folder: f } });
      setList(data.messages || []);
    } catch {
      setList([]);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => { if (me?.cuenta) loadList(folder); }, [me?.cuenta, folder, loadList]);
  useEffect(() => { if (me && !me.es_admin) setConfigOpen(false); }, [me]);

  useEffect(() => {
    if (!me) return;
    if (params.get('compose') !== '1') return;
    const run = async () => {
      try {
        const { data } = await api.get('/xmail/compose-context', {
          params: {
            opportunity_id: params.get('opportunity_id') || undefined,
            to: params.get('to') || undefined,
            subject: params.get('subject') || undefined,
            quote: params.get('quote') ? 1 : 0,
          },
        });
        setDraft({
          to: data.to || '',
          cc: '',
          subject: data.subject || '',
          body: data.body || '',
          opportunity_id: params.get('opportunity_id') || '',
          file_ids: [],
          quote_ids: [],
          in_reply_to: '',
        });
        setComposing(true);
        setCurrent(null);
      } catch {
        setComposing(true);
      }
      const next = new URLSearchParams(params);
      next.delete('compose');
      setParams(next, { replace: true });
    };
    run();
  }, [me, params, setParams]);

  const sema = me?.semaforo || { color: 'gris', texto: '' };
  const isAdmin = Boolean(me?.es_admin);

  const openCompose = async () => {
    try {
      const { data } = await api.get('/xmail/compose-context');
      setDraft({ to: '', cc: '', subject: '', body: data.firma || '', opportunity_id: '', file_ids: [], quote_ids: [], in_reply_to: '' });
    } catch {
      setDraft({ to: '', cc: '', subject: '', body: '', opportunity_id: '', file_ids: [], quote_ids: [], in_reply_to: '' });
    }
    setComposing(true);
    setCurrent(null);
  };

  const activar = async () => {
    setSaving(true);
    try {
      await api.post('/xmail/me/activar');
      toast.success('Tu correo del sistema quedó creado.');
      await loadMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo activar.');
    } finally {
      setSaving(false);
    }
  };

  const enviar = async (asDraft) => {
    setSaving(true);
    try {
      await api.post('/xmail/messages', {
        to: draft.to,
        cc: draft.cc,
        subject: draft.subject,
        body: draft.body,
        opportunity_id: draft.opportunity_id || undefined,
        file_ids: draft.file_ids || [],
        quote_ids: draft.quote_ids || [],
        in_reply_to: draft.in_reply_to || undefined,
        draft: Boolean(asDraft),
      });
      toast.success(asDraft ? 'Borrador guardado.' : 'Correo enviado.');
      setComposing(false);
      await loadMe();
      await loadList(asDraft ? 'drafts' : 'sent');
      setFolder(asDraft ? 'drafts' : 'sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar.');
    } finally {
      setSaving(false);
    }
  };

  const mover = async (id, dest) => {
    try {
      await api.post(`/xmail/messages/${id}/move`, { folder: dest });
      toast.success(dest === 'trash' ? 'Fue a Eliminados (30 días).' : 'Listo.');
      setCurrent(null);
      await loadMe();
      await loadList(folder);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo mover.');
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      toast.error('No pude copiar.');
    }
  };

  const saveTenant = async (modality, extra = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put('/xmail/tenant', {
        modality,
        daily_cap: me?.daily_cap,
        inbound_attach_stage_ids: extra.inbound_attach_stage_ids ?? me?.inbound_attach_stage_ids,
      });
      toast.success(data.aviso || 'Modalidad guardada.');
      await loadMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const emptyHint = useMemo(() => {
    if (!me) return '';
    if (!me.ses?.verificado) return 'mailing';
    if (!me.cuenta) return 'activar';
    return 'ok';
  }, [me]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-5.5rem)] min-h-[560px] flex flex-col gap-3 p-3 md:p-4" data-testid="xmail-page">
      <header className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Mail className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white flex items-center gap-2">
              X-MAIL
              <HelpTip
                title="Qué es X-MAIL"
                content="Tu correo de uno a uno con clientes. No es el mailing de campañas. Sale y entra con el dominio de tu empresa, no con LeadCEM."
              />
            </h1>
            <p className="text-[11px] text-white/50">
              {me?.cuenta?.address || 'Todavía no tienes un correo del sistema'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`${COLOR[sema.color] || COLOR.gris} text-[10px] max-w-[28rem] whitespace-normal`}>
            {sema.texto}
          </Badge>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setConfigOpen((v) => !v)} data-testid="xmail-config-btn" className="border-white/10">
              <Settings2 className="w-4 h-4 mr-1" /> Configurar
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => { loadMe(); if (me?.cuenta) loadList(folder); }} data-testid="xmail-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {emptyHint !== 'ok' && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5" data-testid="xmail-empty">
          {emptyHint === 'mailing' ? (
            <>
              <p className="text-sm text-white font-medium">Primero el dominio de la empresa</p>
              <p className="text-[12px] text-white/60 mt-1 max-w-xl">
                X-MAIL usa el mismo dominio que ya verificas en Mailing. Cuando Mailing diga listo, vuelve aquí y activa tu correo.
              </p>
              <HelpTip title="Por qué espera" content="Si encendiéramos X-MAIL sin dominio verificado, los correos saldrían rechazados o se verían como spam. El administrador lo configura una vez en Mailing." />
            </>
          ) : (
            <>
              <p className="text-sm text-white font-medium">Activa tu correo del sistema</p>
              <p className="text-[12px] text-white/60 mt-1 max-w-xl">
                Se crea solo para ti. Ejemplo: <span className="text-white/90">{me?.ejemplo_a}</span>. No reemplaza el correo de tu tarjeta virtual.
              </p>
              <Button className="mt-3" onClick={activar} disabled={saving} data-testid="xmail-activar">
                Activar mi X-MAIL
              </Button>
            </>
          )}
        </div>
      )}

      {isAdmin && me?.cuenta && (
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[12px] text-white/70 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
          <p>
            Las respuestas del cliente llegan a <span className="text-white">{me.cuenta.address}</span> cuando el MX de
            {' '}<code className="text-white/90">{me.mx_host || 'lcem.tudominio'}</code> apunta a Cloudflare. Eso se publica aquí en Configurar.
            El MX de Google/SiteLock del dominio de campañas no se mueve.
          </p>
        </div>
      )}

      {configOpen && isAdmin && (
        <AdminConfig me={me} onClose={() => setConfigOpen(false)} onSave={saveTenant} saving={saving} copied={copied} onCopy={copy} />
      )}

      {emptyHint === 'ok' && (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)_minmax(0,1.2fr)] gap-3">
          <aside className="rounded-xl border border-white/10 bg-[#12161d] p-2 flex flex-col" data-testid="xmail-folders">
            <Button className="w-full mb-2" onClick={openCompose} data-testid="xmail-compose-btn">
              <PenSquare className="w-4 h-4 mr-2" /> Redactar
            </Button>
            <p className="text-[10px] text-white/40 px-2 mb-1 flex items-center gap-1">
              Bandejas
              <HelpTip title="Bandejas" content={'Entrada: lo que te escriben.\nEnviados: lo que salió.\nSpam: sospechoso.\nEliminados: 30 días y se borra.\nCuarentena: el filtro lo detuvo (14 días).'} />
            </p>
            {(me.folders || []).map((f) => {
              const Icon = FOLDER_ICONS[f.id] || Inbox;
              const n = me.counts?.[f.id] || 0;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => { setFolder(f.id); setComposing(false); setCurrent(null); }}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-[13px] mb-0.5 ${folder === f.id && !composing ? 'bg-primary/15 text-white' : 'text-white/70 hover:bg-white/5'}`}
                  data-testid={`xmail-folder-${f.id}`}
                >
                  <span className="flex items-center gap-2"><Icon className="w-4 h-4" />{f.label}</span>
                  {n > 0 && <span className="text-[10px] text-white/40">{n}</span>}
                </button>
              );
            })}
            <div className="mt-auto p-2 text-[10px] text-white/35">
              Hoy {me.enviados_hoy}/{me.daily_cap}
              <HelpTip title="Tope del día" content="Máximo 40 correos al día y 2 por minuto. X-MAIL es para hablar con un cliente, no para campañas. Las campañas viven en Mailing." />
            </div>
          </aside>

          <section className="rounded-xl border border-white/10 bg-[#12161d] overflow-hidden flex flex-col" data-testid="xmail-list">
            <div className="px-3 py-2 border-b border-white/10 text-[12px] text-white/50">
              {(me.folders || []).find((f) => f.id === folder)?.label || 'Bandeja'}
            </div>
            <div className="overflow-y-auto flex-1">
              {list.length === 0 && (
                <p className="text-[12px] text-white/40 p-6 text-center">Esta bandeja está vacía.</p>
              )}
              {list.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { setCurrent(m); setComposing(false); }}
                  className={`w-full text-left px-3 py-2.5 border-b border-white/5 hover:bg-white/5 ${current?.id === m.id ? 'bg-white/8' : ''}`}
                  data-testid={`xmail-msg-${m.id}`}
                >
                  <p className="text-[13px] text-white truncate">{m.subject || '(sin asunto)'}</p>
                  <p className="text-[11px] text-white/45 truncate">{folder === 'sent' ? m.to_addr : m.from_addr}</p>
                  <p className="text-[10px] text-white/30">{when(m.created_at)}</p>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-xl border border-white/10 bg-[#161b24] overflow-hidden flex flex-col" data-testid="xmail-pane">
            {composing ? (
              <Compose
                draft={draft}
                setDraft={setDraft}
                fromAddr={me.cuenta?.address}
                saving={saving}
                onSend={() => enviar(false)}
                onDraft={() => enviar(true)}
                onClose={() => setComposing(false)}
              />
            ) : current ? (
              <Reader msg={current} folder={folder} onMove={mover} onReply={() => {
                setDraft({
                  to: current.from_addr || current.to_addr || '',
                  cc: '',
                  subject: current.subject?.startsWith('Re:') ? current.subject : `Re: ${current.subject || ''}`,
                  body: `\n\n— Mensaje original —\n${current.body_text || ''}`,
                  opportunity_id: current.opportunity_id || '',
                  file_ids: [],
                  quote_ids: [],
                  in_reply_to: current.message_id || '',
                });
                setComposing(true);
              }} />
            ) : (
              <div className="flex-1 flex items-center justify-center text-white/30 text-[13px] p-8 text-center">
                Elige un correo o redacta uno nuevo.
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Compose({ draft, setDraft, fromAddr, saving, onSend, onDraft, onClose }) {
  return (
    <div className="flex flex-col h-full" data-testid="xmail-compose">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <button type="button" className="text-white/50 md:hidden" onClick={onClose}><ArrowLeft className="w-4 h-4" /></button>
        <p className="text-[13px] font-medium text-white">Redactar</p>
        <HelpTip title="Uno a uno" content="Un solo destinatario en Para. Hasta 4 en Copia. Sin listas, sin BCC, sin CSV. Si necesitas escribirle a muchos, usa Mailing." />
      </div>
      <div className="p-3 space-y-2 overflow-y-auto flex-1">
        <p className="text-[11px] text-white/40">De {fromAddr}</p>
        <div>
          <label className="text-[11px] text-white/50 flex items-center gap-1">Para <HelpTip title="Para" content="El correo de tu cliente. Uno solo." /></label>
          <Input value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="bg-black/20 border-white/10" data-testid="xmail-to" />
        </div>
        <div>
          <label className="text-[11px] text-white/50">Copia (opcional, máx. 4)</label>
          <Input value={draft.cc} onChange={(e) => setDraft({ ...draft, cc: e.target.value })} className="bg-black/20 border-white/10" data-testid="xmail-cc" />
        </div>
        <div>
          <label className="text-[11px] text-white/50">Asunto</label>
          <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="bg-black/20 border-white/10" data-testid="xmail-subject" />
        </div>
        <Textarea rows={12} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="bg-black/20 border-white/10 font-sans" data-testid="xmail-body" />
        <AttachBar draft={draft} setDraft={setDraft} />
      </div>
      <div className="p-3 border-t border-white/10 flex gap-2">
        <Button onClick={onSend} disabled={saving} data-testid="xmail-send">Enviar</Button>
        <Button variant="outline" onClick={onDraft} disabled={saving} className="border-white/10" data-testid="xmail-draft">Guardar borrador</Button>
      </div>
    </div>
  );
}

function Reader({ msg, folder, onMove, onReply }) {
  const blocked = (msg.attachments || []).filter((a) => a.blocked);
  return (
    <div className="flex flex-col h-full" data-testid="xmail-reader">
      <div className="px-4 py-3 border-b border-white/10">
        <p className="text-[15px] text-white font-medium">{msg.subject || '(sin asunto)'}</p>
        <p className="text-[11px] text-white/50 mt-1">De {msg.from_addr}</p>
        <p className="text-[11px] text-white/50">Para {msg.to_addr}</p>
        <p className="text-[10px] text-white/30">{when(msg.created_at)}</p>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {folder === 'quarantine' && (
          <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-100 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Este correo quedó en cuarentena. No abras adjuntos bloqueados. Si es de tu cliente, muévelo a Entrada.
          </div>
        )}
        {blocked.length > 0 && (
          <p className="text-[11px] text-red-300 mb-2">Adjunto bloqueado: {blocked.map((a) => a.name).join(', ')}</p>
        )}
        {msg.attachment_note && <p className="text-[11px] text-amber-200 mb-2">{msg.attachment_note}</p>}
        {(msg.attachments || []).filter((a) => !a.blocked).map((a) => (
          <p key={a.r2_key || a.name} className="text-[12px] text-cyan-300 mb-1">{a.name}</p>
        ))}
        <pre className="whitespace-pre-wrap text-[13px] text-white/80 font-sans">{msg.body_text || ' '}</pre>
      </div>
      <div className="p-3 border-t border-white/10 flex flex-wrap gap-2">
        {folder !== 'sent' && <Button size="sm" onClick={onReply}>Responder</Button>}
        {folder !== 'spam' && folder !== 'trash' && (
          <Button size="sm" variant="outline" className="border-white/10" onClick={() => onMove(msg.id, 'spam')}>Es spam</Button>
        )}
        {folder === 'spam' && (
          <Button size="sm" variant="outline" className="border-white/10" onClick={() => onMove(msg.id, 'inbox')}>No es spam</Button>
        )}
        {folder === 'quarantine' && (
          <Button size="sm" variant="outline" className="border-white/10" onClick={() => onMove(msg.id, 'inbox')}>Sí es de mi cliente</Button>
        )}
        {folder !== 'trash' && (
          <Button size="sm" variant="outline" className="border-white/10" onClick={() => onMove(msg.id, 'trash')}>Eliminar</Button>
        )}
      </div>
    </div>
  );
}

function AdminConfig({ me, onClose, onSave, saving, copied, onCopy }) {
  const modality = me?.tenant?.modality || 'A';
  const domain = me?.tenant?.domain || me?.ses?.dominio || 'tudominio.com';
  return (
    <div className="rounded-xl border border-white/10 bg-[#12161d] p-4 space-y-4" data-testid="xmail-admin">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-white flex items-center gap-2">
          Cómo van a verse los correos de tu equipo
          <HelpTip title="Quién elige" content="Lo elige el administrador de la cuenta, no cada vendedor. Puedes empezar en Forma A y cambiarte después. El alias viejo redirige 30 días." />
        </h2>
        <Button size="sm" variant="ghost" onClick={onClose}>Cerrar</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave('A')}
          className={`text-left rounded-xl border p-4 ${modality === 'A' ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-white/20'}`}
          data-testid="xmail-mod-a"
        >
          <p className="text-[11px] uppercase tracking-wider text-primary mb-1">Forma A · recomendada</p>
          <p className="text-sm text-white font-medium">{me?.ejemplo_a || `asesor@lcem.${domain}`}</p>
          <p className="text-[11px] text-white/55 mt-2">No toca el correo de Google o Microsoft de tu dominio. Los DNS extra van en el subdominio lcem.</p>
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave('B')}
          className={`text-left rounded-xl border p-4 ${modality === 'B' ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-white/20'}`}
          data-testid="xmail-mod-b"
        >
          <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Forma B · opcional</p>
          <p className="text-sm text-white font-medium">{me?.ejemplo_b || `asesor.lcem@${domain}`}</p>
          <p className="text-[11px] text-white/55 mt-2">Misma raíz que tus campañas. Si el MX ya es Google, puedes enviar pero no recibir hasta que decidas el MX.</p>
        </button>
      </div>
      <div>
        <p className="text-[12px] text-white/70 mb-2 flex items-center gap-1">
          DNS que hay que publicar
          <HelpTip title="DNS" content="Forma A agrega registros en lcem.tudominio — no en tudominio. Forma B sí toca el dominio de campañas (MX solo si no es Google). Un solo SPF. Copia y pega; el vendedor no ve esto." />
        </p>
        <div className="space-y-2">
          {(me?.dns || []).map((r, i) => (
            <div key={`${r.tipo}-${r.nombre}-${i}`} className="rounded-lg border border-white/10 p-2 grid grid-cols-[70px_1fr_auto] gap-2 items-start">
              <Badge variant="outline" className="text-[10px] justify-center">{r.tipo}</Badge>
              <div>
                <p className="text-[12px] text-white break-all">{r.nombre}</p>
                <p className="text-[11px] text-white/50 break-all">{r.valor}</p>
                <p className="text-[10px] text-white/35 mt-1">{r.para}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => onCopy(r.valor, String(i))}>
                {copied === String(i) ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          ))}
          {(!me?.dns || me.dns.length === 0) && (
            <p className="text-[12px] text-white/40">Cuando el dominio de Mailing esté verificado, aquí aparecen los registros listos para copiar.</p>
          )}
        </div>
      </div>
      <div>
        <p className="text-[12px] text-white/70 mb-2 flex items-center gap-1">
          Etapas donde el cliente sí puede mandarte archivos
          <HelpTip title="Adjuntos de entrada" content="Marca las etapas de Oportunidades (ej. Negociación, Propuesta). Si no marcas ninguna, los archivos del lead se bloquean. El correo sí llega; el archivo no." />
        </p>
        <div className="flex flex-wrap gap-2">
          {(me.pipeline_stages || []).filter((s) => !s.is_hidden).map((s) => {
            const on = (me.inbound_attach_stage_ids || []).includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                disabled={saving}
                onClick={() => {
                  const cur = new Set(me.inbound_attach_stage_ids || []);
                  if (cur.has(s.id)) cur.delete(s.id); else cur.add(s.id);
                  onSave(me?.tenant?.modality || 'A', { inbound_attach_stage_ids: [...cur] });
                }}
                className={`text-[11px] px-2 py-1 rounded-full border ${on ? 'border-primary bg-primary/20 text-white' : 'border-white/15 text-white/60'}`}
              >
                {s.name}
              </button>
            );
          })}
          {(!me.pipeline_stages || me.pipeline_stages.length === 0) && (
            <p className="text-[11px] text-white/40">Cuando tengas etapas en Oportunidades, aparecen aquí como botones.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function AttachBar({ draft, setDraft }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('galeria');
  const [data, setData] = useState({ files: [], quotes: [] });
  const load = async () => {
    const { data: d } = await api.get('/xmail/adjuntos', { params: { opportunity_id: draft.opportunity_id || undefined } });
    setData(d);
  };
  return (
    <div>
      <button type="button" className="text-[12px] text-cyan-300 flex items-center gap-1" onClick={() => { setOpen(true); load(); }} data-testid="xmail-attach">
        <Paperclip className="w-3 h-3" /> Adjuntar de la galería o una cotización
      </button>
      {(draft.file_ids?.length || draft.quote_ids?.length) ? (
        <p className="text-[11px] text-white/50 mt-1">{(draft.file_ids?.length || 0) + (draft.quote_ids?.length || 0)} adjunto(s)</p>
      ) : null}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#161b24] border border-white/10 rounded-xl w-full max-w-lg p-4" onClick={(e) => e.stopPropagation()} data-testid="xmail-attach-modal">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm text-white font-medium">Adjuntar</p>
              <button type="button" onClick={() => setOpen(false)}><X className="w-4 h-4 text-white/50" /></button>
            </div>
            <div className="flex gap-2 mb-3">
              <Button size="sm" variant={tab === 'galeria' ? 'default' : 'outline'} className="border-white/10" onClick={() => setTab('galeria')}>Galería R2</Button>
              <Button size="sm" variant={tab === 'cotiz' ? 'default' : 'outline'} className="border-white/10" onClick={() => setTab('cotiz')}>Cotizaciones</Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {tab === 'galeria' && (data.files || []).map((f) => {
                const id = f.id;
                const on = (draft.file_ids || []).includes(id);
                return (
                  <button key={id} type="button" className={`w-full text-left text-[12px] px-2 py-2 rounded ${on ? 'bg-primary/20 text-white' : 'text-white/70 hover:bg-white/5'}`} onClick={() => {
                    const cur = new Set(draft.file_ids || []);
                    if (cur.has(id)) cur.delete(id); else cur.add(id);
                    setDraft({ ...draft, file_ids: [...cur] });
                  }}>
                    {f.original_name || f.name}
                  </button>
                );
              })}
              {tab === 'galeria' && (!data.files || data.files.length === 0) && <p className="text-[12px] text-white/40 p-2">No hay archivos en tu galería.</p>}
              {tab === 'cotiz' && (data.quotes || []).map((q) => {
                const on = (draft.quote_ids || []).includes(q.id);
                return (
                  <button key={q.id} type="button" className={`w-full text-left text-[12px] px-2 py-2 rounded ${on ? 'bg-primary/20 text-white' : 'text-white/70 hover:bg-white/5'}`} onClick={() => {
                    const cur = new Set(draft.quote_ids || []);
                    if (cur.has(q.id)) cur.delete(q.id); else cur.add(q.id);
                    setDraft({ ...draft, quote_ids: [...cur] });
                  }}>
                    {q.label || q.id}
                  </button>
                );
              })}
              {tab === 'cotiz' && (!data.quotes || data.quotes.length === 0) && <p className="text-[12px] text-white/40 p-2">Abre X-MAIL desde una oportunidad con cotización para verlas aquí.</p>}
            </div>
            <Button className="mt-3 w-full" onClick={() => setOpen(false)}>Listo</Button>
          </div>
        </div>
      )}
    </div>
  );
}
